import React, { useImperativeHandle, useRef,useState } from "react";
import { RCCHookToast, RCCRefModalProps, RCCServicesApi } from "react-common-components";
import { useForm } from "react-hook-form";
import { useTranslation } from "src/context/Translations/context";
import { useLoaderBackdrop } from "src/context/LoaderBackdrop/context";
import { getValidationErrors, standardErrorMessages } from "src/utils/errors";
import Modal from "src/components/Modal";
import ModalSide from "src/components/Modal/styles";
import { GoalType } from "src/components/ModalIndicator/interface";
import Tabs from "src/components/Tabs";
import { completePermissionType } from "src/components/Permissions/interface";
import stringToNumber from "src/utils/stringToNumber";
import GoalValuestab from "./goalValuesTab";
import { IGoalManagement, IGoalManagementForm, IRefModalGoalManagement } from "./interface";
import GoalManagementFormSchema from "./schemas";

const ModalGoalManagement: React.ForwardRefRenderFunction<IRefModalGoalManagement> = (_, ref) => {
	const { translate,currentLanguage } = useTranslation(); 
	const { setLoading } = useLoaderBackdrop();
	const { addToast} = RCCHookToast();

	const modal = useRef<RCCRefModalProps>(null);
	const [subtitle,setSubtitle] = useState<string>();
	const [goal,setGoal] = useState<IGoalManagement>();
	const [compromise,setCompromise] = useState<IGoalManagement>();
	const [competitiveness,setCompetitiveness] = useState<IGoalManagement>();
	const [userPermission,setUserPermission] = useState<completePermissionType>(completePermissionType.NO_PERMISSION);
	const [onCloseFunction,setOnCloseFunction] = useState<()=>void>();
	
	const { 
		register,
		getValues,
		control,
		formState:{errors}, 
		setError,
		clearErrors,
		setValue,
		reset,
		handleSubmit
	}= useForm<IGoalManagementForm>();

	const requestgoals = async(iqc001Id:number,year:string) => {
		setLoading(true);

		const dataController = await RCCServicesApi.getRequest({
			method: "Indicators/SelectGoalsManagement",
			urlParameters: {
				iqc001Id,
				year
			}
		});
		const list:Array<IGoalManagement> = dataController.data.goals;
		setUserPermission(dataController.data.userPermission.toString());
		setGoal(list.find((x) => x.iqc002Meta === GoalType.GOAL));
		setCompromise(list.find((x) => x.iqc002Meta === GoalType.COMPROMISE));
		setCompetitiveness(list.find((x) => x.iqc002Meta === GoalType.COMPETITIVENESS));
		setLoading(false);
	};

	const actionOpen = async(indicatorId:number,baseYear:string,IndicatorSubTitle?:string,onClose?:()=>void) => {
		try {
			reset();
			setSubtitle(`${translate("Indicador")}: ${IndicatorSubTitle}`);
			setOnCloseFunction(() => onClose);
			await requestgoals(indicatorId,baseYear);
			modal.current?.openModal();
		} catch (error) {
			standardErrorMessages(error,translate("Erro ao buscar valores de metas"), addToast);
			setLoading(false);
		}
	};

	const onSubmit = async(data:any) => {
		try {
		   
			clearErrors();
			modal.current?.setLoadingButton();

			let requestObject = [];
			requestObject.push(data.goal);

			if(data.competitiveness)
				requestObject.push(data.competitiveness);
			if(data.compromise)
				requestObject.push(data.compromise);
	
			requestObject = requestObject.map((req: any) => ({
				...req,
				iqc002Metadefault: stringToNumber(req.iqc002Metadefault, currentLanguage)
			}));

			await GoalManagementFormSchema(translate,currentLanguage).validate({...data}, {
				abortEarly: false
			});

			await RCCServicesApi.postRequest({
				method: "Indicators/InsertUpdateGoalsManagement",
				body:requestObject
			});

			if(onCloseFunction)
				onCloseFunction();

			modal.current?.resetLoadingButton();
			modal.current?.closeModal();

			addToast({
				color: "green",
				description: translate("Metas alteradas com sucesso"),
				title: translate("Sucesso")
			});

		} catch (err: any) {
			modal.current?.resetLoadingButton();
			if(getValidationErrors(err, setError)){
				addToast({
					color: "yellow",
					description: translate("Verifique os campos e tente novamente"),
					title: translate("Atenção")
				});
			}

			standardErrorMessages(err, translate("Erro ao alterar valores de metas"), addToast);
		}
	};

	useImperativeHandle(ref, () => ({
		actionOpen
	}));

	return (
		<Modal
			ref={modal}
			header={{
				icon: {
					name: "dollar"
				},
				title: translate("Gerenciar metas"),
				subtitle
			}}
			onSubmit={handleSubmit(onSubmit)}
			styleInline={ModalSide}
		>
			<Tabs
				name="goals"
				tabInit={0}
				options={
					[
						{
							label: goal?.iqb001Label || translate("Meta melhoria"),
							content: 
								<>
									<GoalValuestab
										goal={goal}
										objectName="goal"
										permission={userPermission}
										register={register}
										control={control}
										errors={errors}
										setValue={setValue}
										getValue={getValues}
									/>
								</>
						},
						{
							label: competitiveness?.iqb001Label || translate("Meta competitividade"),
							content: 
								<>
									<GoalValuestab
										goal={competitiveness}
										objectName="competitiveness"
										permission={userPermission}
										register={register}
										control={control}
										errors={errors}
										setValue={setValue}
										getValue={getValues}
									/>
								</>,
							hidden:!competitiveness
						},
						{
							label: compromise?.iqb001Label || translate("Meta compromisso"),
							content: 
								<>
									<GoalValuestab
										goal={compromise}
										objectName="compromise"
										permission={userPermission}
										register={register}
										control={control}
										errors={errors}
										setValue={setValue}
										getValue={getValues}
									/>
								</>,
							hidden:!compromise
						}
					]
				}
			/>
		</Modal>
	);
};

export default React.forwardRef(ModalGoalManagement);
